import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiRequest } from '../auth/apiClient';
import ComplianceScannerReport from './ComplianceScannerReport';
import EditDetailsModal from './EditDetailsModal';
import { ArchiveButton, ArchivedBadge, RestoreButton } from './ArchiveControls';
import './PropertyDetail.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TAB_OVERVIEW   = 'overview';
const TAB_DOCUMENTS  = 'documents';
const TAB_COMPLIANCE = 'compliance';
const TAB_TENANTS    = 'tenants';
const TAB_MAINT      = 'maintenance';
const TAB_SECTION8   = 'section8';
const TAB_LICENSING  = 'licensing';

// Message 823 (Commit 2) — same in-flight status list as
// backend/routes/landlord.py + property_detail.py. All V2 in-flight
// statuses count as open maintenance for the tab badge and overview
// counter; previous 4-status list was written pre-V2 and silently
// under-counted the true outstanding work.
const IN_FLIGHT_MR_STATUSES = [
  'open', 'acknowledged', 'stage1_running',
  'contractors_notified', 'contractor_assigned',
  'awaiting_tenant_availability', 'pending_tenant_selection',
  'date_proposed', 're_negotiating', 'awaiting_tenant_confirmation',
  'awaiting_operator_routing', 'agent_intervention',
  'quote_rejected', 'in_progress', 'awaiting_quote_approval',
  'awaiting_tenant', 'awaiting_contractor',
];

const STATUS_LABEL = {
  active: 'Active',
  ending_soon: 'Ending soon',
  vacant: 'Vacant',
  eviction_in_progress: 'Eviction in progress',
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB',
      { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return String(d); }
};

const fmtCurrency = (n) => {
  if (n == null) return '—';
  const num = typeof n === 'number' ? n : parseFloat(n);
  if (isNaN(num)) return String(n);
  return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const fullAddress = (p) => {
  if (!p) return '';
  return [p.address_line1, p.address_line2, p.city, p.postcode]
    .filter(Boolean).join(', ');
};

/**
 * TASK 2 — Property Detail page with tabbed layout.
 *
 * Tabs: Overview / Documents / Compliance / Tenants / Maintenance /
 * Section 8 / Licensing.
 */
export default function PropertyDetail() {
  const { property_id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // IMP 10 — Edit details modal.
  const [editOpen, setEditOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [d1, d2] = await Promise.all([
        apiRequest('GET', `${BACKEND_URL}/api/properties/${property_id}/detail`),
        apiRequest('GET', `${BACKEND_URL}/api/properties/${property_id}/documents`),
      ]);
      if (d1.ok) setData(d1.data); else setError(d1.data?.detail || 'Failed to load');
      if (d2.ok) setDocs(d2.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [property_id]); // eslint-disable-line

  if (loading && !data) {
    return <div className="pd-page"><div className="pd-empty">Loading property…</div></div>;
  }
  if (error) {
    return <div className="pd-page"><div className="pd-empty">{error}</div></div>;
  }
  if (!data) {
    return <div className="pd-page"><div className="pd-empty">Property not found.</div></div>;
  }

  const p = data.property || {};
  const tnc = data.current_tenancy;
  const isVacant = !tnc;
  const tenancyStatus = tnc
    ? (data.cases_active && data.cases_active.length > 0 ? 'eviction_in_progress'
       : (() => {
           const ed = tnc.end_date;
           if (!ed) return 'active';
           const days = Math.round((new Date(ed) - new Date()) / 86400000);
           return days <= 60 ? 'ending_soon' : 'active';
         })())
    : 'vacant';

  // 2026-05-29 — When the property is vacant, hide tabs that have no
  // meaningful data:
  //   * Tenants tab          — no tenant on file (re-appears once
  //                            "Start Tenancy" creates a tenancy).
  //   * Section 8 tab        — no tenant to evict, no compliance
  //                            checklist to evaluate.
  //   * The Tenancy Agreement section inside Documents is also hidden
  //     while vacant (handled in `DocumentsTab`).
  // Compliance + Documents + Maintenance + Licensing remain so the
  // agent can prepare the property for letting.
  const allTabs = [
    { id: TAB_OVERVIEW,   label: 'Overview' },
    { id: TAB_DOCUMENTS,  label: 'Documents' },
    { id: TAB_COMPLIANCE, label: 'Compliance', badge: (data.compliance_docs || []).length },
    !isVacant && { id: TAB_TENANTS,    label: 'Tenant',     badge: 1 + (data.previous_tenancies || []).length },
    { id: TAB_MAINT,      label: 'Maintenance', badge: (data.maintenance_requests || [])
                                                        .filter(m => IN_FLIGHT_MR_STATUSES.includes(m.status)).length },
    !isVacant && { id: TAB_SECTION8,   label: 'Section 8',  badge: (data.cases_active || []).length },
    { id: TAB_LICENSING,  label: 'Licensing' },
  ];
  const tabs = allTabs.filter(Boolean);

  return (
    <div className="pd-page" data-testid="property-detail-page">
      <button onClick={() => navigate('/')}
              className="pd-back" data-testid="pd-back">
        ← Back
      </button>

      <div className="pd-header">
        <div>
          <h1 className="pd-title" data-testid="pd-title">
            {fullAddress(p)}
          </h1>
          <div className="pd-subtitle">
            {p.property_type ? `${p.property_type} · ` : ''}
            {p.bedrooms != null ? `${p.bedrooms} bed` : ''}
          </div>
        </div>
        <div>
          <span className={`pd-status-badge pd-status-${tenancyStatus}`}
                data-testid="pd-tenancy-status">
            {STATUS_LABEL[tenancyStatus]}
          </span>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            data-testid="btn-edit-property"
            style={{ marginLeft: 12, background: '#0073EA',
                     color: '#fff', border: 'none', borderRadius: 6,
                     padding: '0.4rem 0.85rem', fontWeight: 600,
                     fontSize: '0.82rem', cursor: 'pointer' }}>
            Edit details
          </button>
          {p.archived ? (
            <span style={{ marginLeft: 8, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <ArchivedBadge />
              <RestoreButton kind="property" id={property_id}
                              onRestored={() => reload()} />
            </span>
          ) : (
            <span style={{ marginLeft: 8 }}>
              <ArchiveButton kind="property" id={property_id}
                              testid="btn-archive-property"
                              onArchived={() => navigate('/properties')} />
            </span>
          )}
        </div>
      </div>

      <EditDetailsModal
        open={editOpen}
        title="Edit property details"
        endpoint={`${BACKEND_URL}/api/properties/${property_id}/details`}
        initial={p}
        fields={[
          { key: 'address_line1', label: 'Address line 1' },
          { key: 'address_line2', label: 'Address line 2' },
          { key: 'city', label: 'City' },
          { key: 'county', label: 'County' },
          { key: 'postcode', label: 'Postcode' },
          { key: 'property_type', label: 'Property type', type: 'select',
            options: [
              { value: 'flat', label: 'Flat' },
              { value: 'house', label: 'House' },
              { value: 'studio', label: 'Studio' },
              { value: 'maisonette', label: 'Maisonette' },
              { value: 'bungalow', label: 'Bungalow' },
              { value: 'other', label: 'Other' },
            ] },
          { key: 'bedrooms', label: 'Bedrooms', type: 'number' },
          { key: 'bathrooms', label: 'Bathrooms', type: 'number' },
          { key: 'furnished', label: 'Furnishing', type: 'select',
            options: [
              { value: 'furnished', label: 'Furnished' },
              { value: 'part_furnished', label: 'Part-furnished' },
              { value: 'unfurnished', label: 'Unfurnished' },
            ] },
          { key: 'rent_amount', label: 'Asking rent (£/mo)', type: 'number' },
          { key: 'council_tax_band', label: 'Council tax band' },
          { key: 'epc_rating', label: 'EPC rating' },
          { key: 'notes', label: 'Internal notes', type: 'textarea', full: true, rows: 3 },
        ]}
        onClose={() => setEditOpen(false)}
        onSaved={() => reload()}
      />

      <div className="pd-tabs" role="tablist">
        {tabs.map(t => (
          <button
            key={t.id}
            role="tab"
            className={`pd-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            data-testid={`pd-tab-${t.id}`}
          >
            {t.label}
            {t.badge ? <span className="pd-tab-badge">{t.badge}</span> : null}
          </button>
        ))}
      </div>

      {activeTab === TAB_OVERVIEW && (
        <OverviewTab data={data} tenancyStatus={tenancyStatus} navigate={navigate}
                     setActiveTab={setActiveTab} isVacant={isVacant}
                     reload={reload} />
      )}

      {activeTab === TAB_DOCUMENTS && (
        <DocumentsTab propertyId={property_id} docs={docs} reload={reload}
                      isVacant={isVacant} />
      )}

      {activeTab === TAB_COMPLIANCE && (
        <ComplianceTab data={data} />
      )}

      {activeTab === TAB_TENANTS && (
        <TenantsTab data={data} />
      )}

      {activeTab === TAB_MAINT && (
        <MaintenanceTab data={data} />
      )}

      {activeTab === TAB_SECTION8 && (
        <Section8Tab data={data} />
      )}

      {activeTab === TAB_LICENSING && (
        <LicensingTab property={p} />
      )}
    </div>
  );
}

// =============================================================================
// Overview tab
// =============================================================================
function OverviewTab({ data, tenancyStatus, setActiveTab, isVacant, reload }) {
  const ll = data.landlord;
  const tnc = data.current_tenancy;
  const compHealth = data.compliance_health;
  const openMaint = (data.maintenance_requests || [])
    .filter(m => IN_FLIGHT_MR_STATUSES.includes(m.status)).length;
  const activeCase = (data.cases_active || [])[0];

  // 2026-05-29 — Start Tenancy modal for vacant properties.
  const [showStartTenancy, setShowStartTenancy] = useState(false);

  const compLabel = {
    green: 'All valid', amber: 'Expiring soon', red: 'Action required', none: 'No documents',
  }[compHealth] || '—';

  // FIX 7 — persistent amber reminder when no tenancy agreement is on file
  // for the active tenancy. Disappears once the agent uploads in Documents.
  const noTenancyAgreement = tnc && !tnc.tenancy_agreement_url
                                 && !tnc.tenancy_agreement_pdf_data;

  return (
    <div data-testid="pd-overview-tab">
      {/* 2026-05-29 — Vacant property: prominent Start Tenancy CTA so
          the agent can capture new tenancy agreement + Right to Rent +
          compliance docs in one go. Hidden once a tenancy is created. */}
      {isVacant && (
        <div data-testid="pd-start-tenancy-banner"
             style={{ background: '#ecfdf5', border: '1px solid #6ee7b7',
                      borderRadius: 10, padding: '1rem 1.15rem',
                      marginBottom: '1rem', display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between',
                      gap: '0.85rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#065f46',
                          marginBottom: '0.3rem' }}>
              This property is currently vacant.
            </div>
            <div style={{ fontSize: '0.88rem', color: '#047857' }}>
              When you've found a tenant click <strong>Start Tenancy</strong> to
              record the tenancy agreement, Right to Rent check and compliance
              documents in one place.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowStartTenancy(true)}
            data-testid="pd-start-tenancy"
            style={{ background: '#059669', color: '#fff', border: 'none',
                     padding: '0.6rem 1.2rem', borderRadius: 6,
                     fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                     whiteSpace: 'nowrap' }}>
            Start Tenancy
          </button>
        </div>
      )}

      {showStartTenancy && (
        <StartTenancyModal
          propertyId={data.property?._id || data.property?.id}
          landlord={ll}
          onClose={() => setShowStartTenancy(false)}
          onSaved={() => { setShowStartTenancy(false); reload && reload(); }}
        />
      )}

      {!isVacant && noTenancyAgreement && (
        <div data-testid="pd-no-tenancy-agreement-reminder"
             style={{ background: '#fff7ed', border: '1px solid #fdba74',
                      borderRadius: 8, padding: '0.7rem 1rem',
                      marginBottom: '1rem', color: '#9a3412',
                      fontSize: '0.9rem', display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between',
                      gap: '0.85rem', flexWrap: 'wrap' }}>
          <div>
            <strong>No tenancy agreement on file.</strong>{' '}
            Upload it in the Documents tab to ensure it is available for
            Section 8 proceedings.
          </div>
          <button
            type="button"
            onClick={() => setActiveTab(TAB_DOCUMENTS)}
            data-testid="pd-go-upload-tenancy-agreement"
            style={{ background: '#9a3412', color: '#fff', border: 'none',
                     padding: '0.45rem 0.9rem', borderRadius: 4,
                     fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer',
                     whiteSpace: 'nowrap' }}>
            Open Documents
          </button>
        </div>
      )}
      {/* Landlord */}
      <div className="pd-card">
        <h3>Landlord</h3>
        {ll ? (
          <>
            <div className="pd-card-row">
              <span className="label">Name</span>
              <span className="value">
                <Link to={`/landlords/${ll.id || ll._id}`}>
                  {ll.full_name || ll.company_name || '(unnamed)'}
                </Link>
              </span>
            </div>
            {ll.company_name && ll.full_name && (
              <div className="pd-card-row">
                <span className="label">Company</span>
                <span className="value">{ll.company_name}</span>
              </div>
            )}
            <div className="pd-card-row">
              <span className="label">Email</span>
              <span className="value">{ll.email || '—'}</span>
            </div>
            <div className="pd-card-row">
              <span className="label">Phone</span>
              <span className="value">{ll.phone_number || '—'}</span>
            </div>
          </>
        ) : (
          <div>
            <p style={{ color: '#b91c1c', marginBottom: '0.75rem' }}>No landlord linked.</p>
            <button className="pd-btn" data-testid="pd-add-landlord-btn"
                    onClick={() => alert('Add a landlord from Settings ▸ Landlords or the dashboard banner.')}>
              Add Landlord
            </button>
          </div>
        )}
      </div>

      {/* Current tenancy */}
      <div className="pd-card">
        <h3>Current Tenancy</h3>
        {tnc ? (
          <>
            <div className="pd-card-row">
              <span className="label">Tenant</span>
              <span className="value">
                {tnc.tenant ? (
                  <Link to={`/tenants/${tnc.tenant.id}`}>
                    {tnc.tenant.first_name} {tnc.tenant.last_name}
                  </Link>
                ) : '—'}
              </span>
            </div>
            <div className="pd-card-row">
              <span className="label">Rent</span>
              <span className="value">
                {fmtCurrency(tnc.rent_amount)} / {tnc.rent_frequency || tnc.rent_period || 'monthly'}
              </span>
            </div>
            <div className="pd-card-row">
              <span className="label">Start date</span>
              <span className="value">{fmtDate(tnc.start_date)}</span>
            </div>
            <div className="pd-card-row">
              <span className="label">End date</span>
              <span className="value">{fmtDate(tnc.end_date)}</span>
            </div>
            <div className="pd-card-row">
              <span className="label">Status</span>
              <span className="value">
                <span className={`pd-status-badge pd-status-${tenancyStatus}`}>
                  {{
                    active: 'Active',
                    ending_soon: 'Ending soon',
                    vacant: 'Vacant',
                    eviction_in_progress: 'Eviction in progress',
                  }[tenancyStatus]}
                </span>
              </span>
            </div>
          </>
        ) : (
          <div className="pd-empty">No active tenancy.</div>
        )}
      </div>

      {/* Quick stats */}
      <div className="pd-card">
        <h3>Quick Stats</h3>
        <div className="pd-grid">
          <div className="pd-stat">
            <div className="pd-stat-label">Compliance</div>
            <div className="pd-stat-value" data-testid="pd-stat-compliance">
              <span className={`pd-status-badge pd-status-${
                compHealth === 'green' ? 'active' :
                compHealth === 'amber' ? 'ending_soon' :
                compHealth === 'red'   ? 'eviction_in_progress' :
                                         'vacant'
              }`}>
                {compLabel}
              </span>
            </div>
          </div>
          <div className="pd-stat">
            <div className="pd-stat-label">Open Maintenance</div>
            <div className="pd-stat-value" data-testid="pd-stat-maintenance">
              {openMaint}
            </div>
          </div>
          <div className="pd-stat">
            <div className="pd-stat-label">Active Section 8</div>
            <div className="pd-stat-value" data-testid="pd-stat-section8">
              {activeCase ? `Stage ${activeCase.current_stage}` : '—'}
            </div>
          </div>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <button className="pd-btn-secondary pd-btn"
                  onClick={() => setActiveTab(TAB_DOCUMENTS)}
                  data-testid="pd-go-documents">
            Open Documents
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Documents tab — TASK 3
// =============================================================================
const COMPLIANCE_DOC_TYPES = [
  { key: 'epc', label: 'EPC' },
  { key: 'gas_safety', label: 'Gas Safety Certificate' },
  { key: 'eicr', label: 'EICR' },
];

const S8_DOC_LABELS = {
  section8_notice:        'Section 8 Notice (Form 3A)',
  signed_notice:          'Signed Section 8 Notice',
  n5:                     'Form N5 — Claim',
  n119:                   'Form N119 — Particulars',
  n215:                   'Form N215 — Service',
  court_bundle:           'Court Bundle',
  tenant_bundle:          'Tenant Bundle',
  case_summary:           'Case Summary',
  audit_export:           'Audit Export',
};

function DocumentsTab({ propertyId, docs, reload, isVacant }) {
  const [uploading, setUploading] = useState(false);

  const onUploadTenancy = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Tenancy agreements must be uploaded as PDF.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${BACKEND_URL}/api/properties/${propertyId}/upload-tenancy-agreement`,
        { method: 'POST', credentials: 'include', body: fd });
      const text = await res.text();
      if (!res.ok) {
        const detail = text ? (JSON.parse(text).detail || text) : 'Upload failed';
        alert(detail);
      } else {
        await reload();
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const view = (url) => window.open(BACKEND_URL + url, '_blank', 'noopener');

  const ta = docs?.tenancy_agreement;
  const compDocs = docs?.compliance_docs || [];
  const s8Docs = docs?.section8_docs || [];

  // Build per-compliance-doc-type lookup so we can show missing ones too.
  const compByType = {};
  compDocs.forEach(d => { compByType[d.doc_type] = d; });

  return (
    <div data-testid="pd-documents-tab">
      {/* ===== TENANCY AGREEMENT ===== */}
      {/* 2026-05-29 — Hidden while the property is vacant. Re-appears
          once "Start Tenancy" is clicked and a tenancy exists. */}
      {!isVacant && (
      <div className="pd-card">
        <h3>Tenancy Agreement</h3>
        {ta && ta.has_file ? (
          <div className="pd-doc-row" data-testid="pd-doc-tenancy-agreement">
            <div className="doc-meta">
              <div className="doc-name">Signed Tenancy Agreement</div>
              <div className="doc-sub">
                Uploaded {fmtDate(ta.uploaded_at)}
                {ta.source ? ` · ${ta.source.replace(/_/g, ' ')}` : ''}
              </div>
            </div>
            <div className="pd-doc-actions">
              <button className="pd-btn" onClick={() => view(ta.file_url)}
                      data-testid="pd-view-tenancy-agreement">
                View Agreement
              </button>
              <label className="pd-btn-secondary pd-btn"
                     style={{ display: 'inline-flex', alignItems: 'center' }}>
                {uploading ? 'Uploading…' : 'Replace Agreement'}
                <input type="file" accept=".pdf" hidden
                       onChange={onUploadTenancy}
                       disabled={uploading}
                       data-testid="pd-replace-tenancy-agreement" />
              </label>
            </div>
          </div>
        ) : (
          <>
            <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '0.85rem' }}>
              No tenancy agreement on file. Upload the signed PDF so it can be
              referenced during compliance, Section 8, and bundle assembly.
            </p>
            <label className="pd-btn"
                   style={{ display: 'inline-flex', alignItems: 'center' }}>
              {uploading ? 'Uploading…' : 'Upload Tenancy Agreement'}
              <input type="file" accept=".pdf" hidden
                     onChange={onUploadTenancy}
                     disabled={uploading}
                     data-testid="pd-upload-tenancy-agreement" />
            </label>
          </>
        )}
      </div>
      )}

      {/* ===== COMPLIANCE DOCUMENTS ===== */}
      <div className="pd-card">
        <h3>Compliance Documents</h3>
        {COMPLIANCE_DOC_TYPES.map(t => {
          const d = compByType[t.key];
          const expiry = d?.expiry_date;
          const expired = expiry && new Date(expiry) < new Date();
          const expiringSoon = expiry &&
            (new Date(expiry) - new Date()) / 86400000 < 90 && !expired;
          return (
            <div key={t.key} className="pd-doc-row"
                 data-testid={`pd-comp-${t.key}`}>
              <div className="doc-meta">
                <div className="doc-name">
                  {t.label}{' '}
                  {!d ? <span className="pd-badge-missing">Missing</span>
                      : expired ? <span className="pd-badge-red">Expired</span>
                      : expiringSoon ? <span className="pd-badge-amber">Expiring</span>
                      : <span className="pd-badge-valid">Valid</span>}
                </div>
                <div className="doc-sub">
                  {d ? (
                    <>
                      {expiry ? `Expires ${fmtDate(expiry)}` : 'On file'}
                      {d.served_to_tenant ? ' · Served to tenant' : ''}
                    </>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>Not uploaded yet</span>
                  )}
                </div>
              </div>
              <div className="pd-doc-actions">
                {d && (
                  <button className="pd-btn"
                          onClick={() => view(`/api/compliance/doc-file/${propertyId}/${t.key}`)}
                          data-testid={`pd-view-comp-${t.key}`}>
                    View Document
                  </button>
                )}
                <a className="pd-btn-secondary pd-btn"
                   href="#"
                   onClick={(e) => {
                     e.preventDefault();
                     alert('Upload a new compliance version from Dashboard ▸ Compliance Status.');
                   }}
                   data-testid={`pd-upload-comp-${t.key}`}>
                  {d ? 'Upload New Version' : 'Upload'}
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== SECTION 8 DOCUMENTS ===== */}
      <div className="pd-card">
        <h3>Section 8 Documents</h3>
        {s8Docs.length === 0 ? (
          <div className="pd-empty">No Section 8 documents on file.</div>
        ) : (
          s8Docs.map(d => {
            const label = d.doc_label || S8_DOC_LABELS[d.doc_type] ||
                          (d.doc_type || 'Document').replace(/_/g, ' ');
            const url = d.file_url || `/api/section8/case-document/${d._id || d.id}`;
            return (
              <div key={d._id || d.id} className="pd-doc-row"
                   data-testid={`pd-s8-doc-${d._id || d.id}`}>
                <div className="doc-meta">
                  <div className="doc-name">{label}</div>
                  <div className="doc-sub">
                    Generated {fmtDate(d.generated_at || d.created_at)}
                    {d.case_id ? ` · Case ${String(d.case_id).slice(-6)}` : ''}
                  </div>
                </div>
                <div className="pd-doc-actions">
                  <button className="pd-btn" onClick={() => view(url)}>
                    View
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Compliance tab — existing grid (read-only summary; full upload still on dashboard)
// =============================================================================
function ComplianceTab({ data }) {
  const compByType = {};
  (data.compliance_docs || []).forEach(d => { compByType[d.doc_type] = d; });

  // 2026-05-29 — Run Compliance Check button (on-demand scan).
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const propertyId = data?.property?._id || data?.property?.id;

  // Load latest scan on mount so the report persists across navigation.
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/compliance/scanner/latest/property/${propertyId}`,
          { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          if (d.scan) setScan(d.scan);
        }
      } catch {}
    })();
  }, [propertyId]);

  const runScan = async () => {
    if (!propertyId) return;
    setScanning(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/compliance/scanner/property/${propertyId}`,
        { method: 'POST', credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setScan(d);
      } else {
        alert('Compliance scan failed.');
      }
    } finally {
      setScanning(false);
    }
  };

  const refresh = async () => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/compliance/scanner/latest/property/${propertyId}`,
        { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        if (d.scan) setScan(d.scan);
      }
    } catch {}
  };

  return (
    <div data-testid="pd-compliance-tab">
      <div className="pd-card">
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Compliance Status</h3>
          <button
            data-testid="pd-run-compliance-scan"
            onClick={runScan}
            disabled={scanning}
            style={{
              background: scanning ? '#94a3b8' : '#0073EA',
              color: '#fff',
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: 6,
              cursor: scanning ? 'wait' : 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            {scanning ? 'Scanning…' : 'Run Compliance Check'}
          </button>
        </div>
        {COMPLIANCE_DOC_TYPES.map(t => {
          const d = compByType[t.key];
          return (
            <div key={t.key} className="pd-card-row">
              <span className="label">{t.label}</span>
              <span className="value">
                {d ? (
                  <>
                    {d.expiry_date ? `Expires ${fmtDate(d.expiry_date)}` : 'On file'}
                    {' · '}
                    {d.served_to_tenant ? <span className="pd-badge-valid">Served to tenant</span>
                                        : <span className="pd-badge-amber">Not served</span>}
                  </>
                ) : (
                  <span className="pd-badge-missing">Missing</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {scan && (
        <>
          {/* 2026-05-31 — Hidden file input used by the per-item Upload
              buttons in the Compliance Health Report. Multi-file upload
              (no client-side limit) — backend AI auto-types each file. */}
          <input
            type="file"
            id="cs-upload-input-hidden"
            multiple
            accept="application/pdf,image/png,image/jpeg"
            style={{ display: 'none' }}
            data-testid="cs-upload-input-hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              for (const f of files) {
                try {
                  const fd = new FormData();
                  fd.append('property_id', propertyId);
                  fd.append('file', f);
                  await fetch(`${BACKEND_URL}/api/compliance/upload-auto`, {
                    method: 'POST', credentials: 'include', body: fd,
                  });
                } catch (_err) { /* per-file failure is non-fatal */ }
              }
              e.target.value = '';   // reset so same file can be reselected
              // Re-run scan to surface the new docs.
              await runScan();
            }}
          />
          <ComplianceScannerReport
            scans={[scan]}
            summary={{
              red: scan.overall_status === 'red' ? 1 : 0,
              amber: scan.overall_status === 'amber' ? 1 : 0,
              green: scan.overall_status === 'green' ? 1 : 0,
              total_properties: 1,
            }}
            onRefresh={refresh}
            onActionClick={() => {
              // Open the hidden multi-file picker for ANY upload button.
              document.getElementById('cs-upload-input-hidden')?.click();
            }}
            title="Compliance Health Report"
            subtitle={`Last scanned ${new Date(scan.triggered_at).toLocaleString()}`}
          />
        </>
      )}
    </div>
  );
}

// =============================================================================
// Tenants tab
// =============================================================================
function TenantsTab({ data }) {
  const cur = data.current_tenancy;
  const prev = data.previous_tenancies || [];
  return (
    <div data-testid="pd-tenants-tab">
      <div className="pd-card">
        <h3>Current Tenant</h3>
        {cur && cur.tenant ? (
          <div className="pd-doc-row">
            <div className="doc-meta">
              <div className="doc-name">
                <Link to={`/tenants/${cur.tenant.id}`}>
                  {cur.tenant.first_name} {cur.tenant.last_name}
                </Link>
              </div>
              <div className="doc-sub">
                Started {fmtDate(cur.start_date)}
                {cur.end_date ? ` · Ends ${fmtDate(cur.end_date)}` : ''}
                {' · '}{fmtCurrency(cur.rent_amount)}/{cur.rent_frequency || 'month'}
              </div>
            </div>
          </div>
        ) : (
          <div className="pd-empty">No active tenant.</div>
        )}
      </div>
      <div className="pd-card">
        <h3>Previous Tenancies</h3>
        {prev.length === 0 ? (
          <div className="pd-empty">No previous tenancies.</div>
        ) : prev.map(t => (
          <div key={t.id || t._id} className="pd-doc-row">
            <div className="doc-meta">
              <div className="doc-name">
                {t.tenant ? (
                  <Link to={`/tenants/${t.tenant.id}`}>
                    {t.tenant.first_name} {t.tenant.last_name}
                  </Link>
                ) : '(unknown tenant)'}
              </div>
              <div className="doc-sub">
                {fmtDate(t.start_date)} → {fmtDate(t.end_date)}
                {' · status '}{t.status}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Maintenance tab
// =============================================================================
function MaintenanceTab({ data }) {
  const list = data.maintenance_requests || [];
  if (list.length === 0) {
    return (
      <div className="pd-card pd-empty" data-testid="pd-maintenance-tab">
        No maintenance requests for this property.
      </div>
    );
  }
  const PRI_COL = { high: '#dc2626', medium: '#f59e0b', low: '#16a34a' };
  return (
    <div data-testid="pd-maintenance-tab">
      {list.map(m => {
        const days = m.created_at
          ? Math.max(0, Math.floor((Date.now() - new Date(m.created_at)) / 86400000))
          : 0;
        return (
          <div key={m._id || m.id} className="pd-doc-row">
            <div className="doc-meta">
              <div className="doc-name">
                {m.title || m.category || 'Maintenance request'}
                <span style={{ marginLeft: '0.5rem', color: PRI_COL[m.priority] || '#475569',
                               fontSize: '0.78rem', fontWeight: 600 }}>
                  {(m.priority || 'medium').toUpperCase()}
                </span>
              </div>
              <div className="doc-sub">
                {(m.description || '').slice(0, 120)}
                {(m.description || '').length > 120 ? '…' : ''}
                {' · '}{days} day{days !== 1 ? 's' : ''} open · status: {m.status}
              </div>
            </div>
            <div className="pd-doc-actions">
              <Link to={`/maintenance/${m._id || m.id}`} className="pd-btn">
                View Request
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Section 8 tab
// =============================================================================
function Section8Tab({ data }) {
  const active = data.cases_active || [];
  const closed = data.cases_closed || [];
  return (
    <div data-testid="pd-section8-tab">
      <div className="pd-card">
        <h3>Active Section 8 Case</h3>
        {active.length === 0 ? (
          <div className="pd-empty">No active Section 8 case.</div>
        ) : active.map(c => (
          <div key={c._id || c.id} className="pd-doc-row">
            <div className="doc-meta">
              <div className="doc-name">Stage {c.current_stage} — {c.breach_type || 'Section 8'}</div>
              <div className="doc-sub">
                Arrears: {fmtCurrency(c.arrears_at_notice_date)} ·
                Notice end: {fmtDate(c.notice_period_end)}
              </div>
            </div>
            <div className="pd-doc-actions">
              <Link to={`/section8/case/${c._id || c.id}`} className="pd-btn">
                View Case
              </Link>
            </div>
          </div>
        ))}
      </div>
      <div className="pd-card">
        <h3>Previous Cases</h3>
        {closed.length === 0 ? (
          <div className="pd-empty">No previous Section 8 cases.</div>
        ) : closed.map(c => (
          <div key={c._id || c.id} className="pd-doc-row">
            <div className="doc-meta">
              <div className="doc-name">Stage {c.current_stage} — {c.breach_type || 'Section 8'}</div>
              <div className="doc-sub">
                Created {fmtDate(c.created_at)}
              </div>
            </div>
            <div className="pd-doc-actions">
              <Link to={`/section8/case/${c._id || c.id}`} className="pd-btn-secondary pd-btn">
                View
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Licensing tab
// =============================================================================
function LicensingTab({ property }) {
  const p = property;
  return (
    <div data-testid="pd-licensing-tab">
      <div className="pd-card">
        <h3>HMO Licence</h3>
        <div className="pd-card-row">
          <span className="label">Property is HMO</span>
          <span className="value">{p.is_hmo ? 'Yes' : 'No'}</span>
        </div>
        {p.is_hmo && (
          <>
            <div className="pd-card-row">
              <span className="label">Licence number</span>
              <span className="value">{p.hmo_licence_number || '—'}</span>
            </div>
            <div className="pd-card-row">
              <span className="label">Issuing council</span>
              <span className="value">{p.hmo_licence_issuing_council || '—'}</span>
            </div>
            <div className="pd-card-row">
              <span className="label">Expiry</span>
              <span className="value">{fmtDate(p.hmo_licence_expiry)}</span>
            </div>
          </>
        )}
      </div>
      <div className="pd-card">
        <h3>Selective Licence</h3>
        <div className="pd-card-row">
          <span className="label">Licence number</span>
          <span className="value">{p.selective_licence_number || '—'}</span>
        </div>
        <div className="pd-card-row">
          <span className="label">Expiry</span>
          <span className="value">{fmtDate(p.selective_licence_expiry)}</span>
        </div>
      </div>
      <div className="pd-card">
        <h3>Additional Licence</h3>
        <div className="pd-card-row">
          <span className="label">Licence number</span>
          <span className="value">{p.additional_licence_number || '—'}</span>
        </div>
        <div className="pd-card-row">
          <span className="label">Expiry</span>
          <span className="value">{fmtDate(p.additional_licence_expiry)}</span>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// StartTenancyModal — 2026-05-29
// Captures tenant + Right to Rent + tenancy terms + tenancy agreement +
// compliance docs in one flow when an agent clicks "Start Tenancy" on a
// vacant property. Persists to /api/tenancies/start-on-property/{id}.
// =============================================================================
function StartTenancyModal({ propertyId, landlord, onClose, onSaved }) {
  const [t, setT] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    right_to_rent_checked: true,
    right_to_rent_check_date: new Date().toISOString().slice(0, 10),
    right_to_rent_expiry: '',
  });
  const [tn, setTn] = useState({
    start_date: '', rent_amount: '', rent_frequency: 'monthly',
    rent_due_day: 1, deposit_amount: '', deposit_scheme: 'DPS',
  });
  const [agreement, setAgreement] = useState({ name: null, dataUrl: null });
  const [complianceFiles, setComplianceFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const setTen = (k) => (e) => setT(prev => ({ ...prev, [k]: e.target.value }));
  const setTnc = (k) => (e) => setTn(prev => ({ ...prev, [k]: e.target.value }));

  const onAgreement = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setAgreement({ name: f.name, dataUrl: r.result });
    r.readAsDataURL(f);
  };
  const onCompliance = (e) => {
    const files = Array.from(e.target.files || []);
    setComplianceFiles(prev => [...prev, ...files]);
  };

  const save = async () => {
    setErr(null);
    if (!t.first_name || !t.last_name) {
      setErr('Tenant first and last name are required.'); return;
    }
    if (!tn.start_date || !tn.rent_amount) {
      setErr('Tenancy start date and rent amount are required.'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/tenancies/start-on-property/${propertyId}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant: {
              first_name: t.first_name.trim(),
              last_name: t.last_name.trim(),
              email: t.email.trim() || null,
              phone: t.phone.trim() || null,
              right_to_rent_checked: !!t.right_to_rent_checked,
              right_to_rent_check_date: t.right_to_rent_check_date || null,
              right_to_rent_expiry: t.right_to_rent_expiry || null,
            },
            tenancy: {
              start_date: tn.start_date,
              rent_amount: parseFloat(tn.rent_amount),
              rent_frequency: tn.rent_frequency,
              rent_due_day: parseInt(tn.rent_due_day, 10) || 1,
              deposit_amount: parseFloat(tn.deposit_amount || 0),
              deposit_scheme: tn.deposit_scheme,
              tenancy_agreement_url: agreement.dataUrl,
            },
          }),
        });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (!res.ok) {
        setErr(data.detail || 'Could not start tenancy.');
        return;
      }
      for (const f of complianceFiles) {
        try {
          const fd = new FormData();
          fd.append('property_id', propertyId);
          fd.append('file', f);
          await fetch(`${BACKEND_URL}/api/compliance/upload-auto`, {
            method: 'POST', credentials: 'include', body: fd,
          });
        } catch { /* non-fatal */ }
      }
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  const FIELD = { display: 'flex', flexDirection: 'column', gap: '0.3rem' };
  const LBL = { fontSize: '0.82rem', color: '#475569', fontWeight: 500 };
  const INPUT = { padding: '0.5rem 0.7rem', borderRadius: 6,
                  border: '1px solid #cbd5e1', fontSize: '0.9rem' };

  return (
    <div data-testid="pd-start-tenancy-modal"
         style={{ position: 'fixed', inset: 0,
                  background: 'rgba(15,23,42,0.55)', zIndex: 9999,
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', padding: '1.5rem' }}>
      <div style={{ background: '#fff', borderRadius: 12,
                    maxWidth: 760, width: '100%',
                    maxHeight: '90vh', overflow: 'auto',
                    padding: '1.5rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '1.1rem' }}>
          Start Tenancy{landlord ? ` — Landlord: ${landlord.full_name || landlord.company_name || ''}` : ''}
        </h3>

        <h4 style={{ marginTop: '1rem', fontSize: '0.95rem' }}>Tenant</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div style={FIELD}>
            <span style={LBL}>First name *</span>
            <input style={INPUT} value={t.first_name} onChange={setTen('first_name')} data-testid="pd-st-first" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Last name *</span>
            <input style={INPUT} value={t.last_name} onChange={setTen('last_name')} data-testid="pd-st-last" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Email</span>
            <input style={INPUT} type="email" value={t.email} onChange={setTen('email')} data-testid="pd-st-email" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Phone</span>
            <input style={INPUT} value={t.phone} onChange={setTen('phone')} placeholder="+44…" data-testid="pd-st-phone" />
          </div>
        </div>

        <h4 style={{ marginTop: '1rem', fontSize: '0.95rem' }}>Right to Rent</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <label style={{ gridColumn: '1 / -1', display: 'flex',
                          alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={!!t.right_to_rent_checked}
                   onChange={(e) => setT(p => ({ ...p, right_to_rent_checked: e.target.checked }))}
                   data-testid="pd-st-rtr-checked" />
            I have completed a Right to Rent check on this tenant
          </label>
          <div style={FIELD}>
            <span style={LBL}>Check date</span>
            <input style={INPUT} type="date" value={t.right_to_rent_check_date}
                   onChange={setTen('right_to_rent_check_date')} data-testid="pd-st-rtr-date" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Expiry (time-limited only)</span>
            <input style={INPUT} type="date" value={t.right_to_rent_expiry}
                   onChange={setTen('right_to_rent_expiry')} data-testid="pd-st-rtr-expiry" />
          </div>
        </div>

        <h4 style={{ marginTop: '1rem', fontSize: '0.95rem' }}>Tenancy terms</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div style={FIELD}>
            <span style={LBL}>Start date *</span>
            <input style={INPUT} type="date" value={tn.start_date}
                   onChange={setTnc('start_date')} data-testid="pd-st-start" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Rent amount (£) *</span>
            <input style={INPUT} type="number" min={0} value={tn.rent_amount}
                   onChange={setTnc('rent_amount')} data-testid="pd-st-rent" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Rent frequency *</span>
            <select style={INPUT} value={tn.rent_frequency}
                    onChange={setTnc('rent_frequency')} data-testid="pd-st-freq">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div style={FIELD}>
            <span style={LBL}>Rent due day *</span>
            <input style={INPUT} type="number" min={1} max={31} value={tn.rent_due_day}
                   onChange={setTnc('rent_due_day')} data-testid="pd-st-dueday" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Deposit amount (£)</span>
            <input style={INPUT} type="number" min={0} value={tn.deposit_amount}
                   onChange={setTnc('deposit_amount')} data-testid="pd-st-deposit" />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Deposit scheme</span>
            <select style={INPUT} value={tn.deposit_scheme}
                    onChange={setTnc('deposit_scheme')} data-testid="pd-st-scheme">
              <option value="DPS">DPS</option>
              <option value="MyDeposits">MyDeposits</option>
              <option value="TDS">TDS</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <h4 style={{ marginTop: '1rem', fontSize: '0.95rem' }}>Documents</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div style={{ background: '#f8fafc', padding: '0.75rem',
                        border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={LBL}>Upload Tenancy Agreement (PDF)</div>
            <input type="file" accept="application/pdf" onChange={onAgreement} data-testid="pd-st-agreement" />
            {agreement.name && <div style={{ fontSize: '0.78rem', color: '#16a34a' }}>✓ {agreement.name}</div>}
          </div>
          <div style={{ background: '#f8fafc', padding: '0.75rem',
                        border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={LBL}>Upload Compliance Documents (PDF/JPG/PNG)</div>
            <input type="file" multiple accept="application/pdf,image/png,image/jpeg"
                   onChange={onCompliance} data-testid="pd-st-compliance" />
            <div style={{ fontSize: '0.78rem', color: '#475569' }}>
              {complianceFiles.length > 0
                ? `${complianceFiles.length} file(s) ready · AI will identify document type`
                : 'EPC, Gas Safety, EICR, Misc — drop them all together.'}
            </div>
          </div>
        </div>

        {err && <div role="alert" data-testid="pd-st-error"
                     style={{ marginTop: '0.75rem', color: '#b91c1c', fontSize: '0.85rem' }}>{err}</div>}

        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onClose} data-testid="pd-st-cancel"
                  style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1',
                           padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 600,
                           fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pd-st-save"
                  style={{ background: '#0073EA', color: '#fff', border: 'none',
                           padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 600,
                           fontSize: '0.9rem', cursor: 'pointer' }}>
            {saving ? 'Starting…' : 'Start Tenancy'}
          </button>
        </div>
      </div>
    </div>
  );
}
